import React from "react";
import {
  Box,
  Flex,
  Grid,
  Text,
  GridItem,
  Divider,
  useBreakpointValue,
  Container,
} from "@chakra-ui/react";
import Button from "@components/common/Button";
import { theme } from "src/styles/theme";
import Tab from "@components/Sales/Tab";
import CustomCalendar from "./CustomCalendar";
import CustomComitions from "./CustomComitions";
import { MdOutlineArrowOutward } from "react-icons/md";
import CustomResume from "./CustomResume";
import CustomOportunityByStatus from "./CustomOportunityByStatus";
import ButtonPopover from "@components/Sales/ButtonPopover";
import { useNavigate } from "react-router-dom";
import { LoginContext } from "@context/loginContext";
import { UsuallyContext } from "@context/usuallyContex";
import { OportunityContext } from "@context/oportunityContex";
import type { ICalendario, IFixos } from "src/@types/Home";
import { formatMoney } from "@helpers/format";
import CustomCreditRatingByStatus from "./CustomCreditRatingByStatus";
import homeServiceInstance from "@services/Home/homeService";
import Spinner from "@components/common/Spinner";
import { ptBR } from "react-day-picker/locale";
import { format } from "date-fns";
import Alert from "@components/common/Alert";
import CustomRating from "./CustomRating";
import chatServiceInstance from "@services/ChatBot/chatService";
import CustomSchedulle from "./CustomSchedulle";
import CustomTasks from "./CustomTasks";
import { BsPlusLg } from "react-icons/bs";

const fixos = {
  oportunidadesPendentes: [
    {
      fase: "",
      quantidade: 0,
    },
  ],
  oportunidadesPorStatus: [
    {
      fase: "",
      quantidade: 0,
    },
  ],
  analiseDeCreditoPorStatus: [
    {
      status: "",
      quantidade: 0,
    },
  ],
  oportunidadesPorRanking: [
    {
      rank: "",
      quantidade: 0,
    },
  ],
  comissoes: [
    {
      statusPagamento: "",
      idOportunidade: "",
      cliente: "",
      dataDePagamento: "",
      valorHonorarios: 0,
    },
  ],
};

const calendario = {
  agendamentos: [
    {
      opportunityIdSalesforce: "",
      oportunidadeId: "",
      nomeCorretor: "",
      nomePDV: "",
      nomeEmpreendimento: "",
      gerenteRegional: "",
      idAgendamentoSalesforce: "",
      dataAgendamento: "",
      horaAgendamento: "",
      pdv: "",
      diretorDeVendas: "",
      relativoA: "",
      tipoDeVisita: "",
      codigoDoAgendamento: "",
      localizacaoDoStand: "",
      unidadeDeNegocio: "",
      regional: "",
      participantes: "",
      assunto: "",
      visitaRealizada: false,
      observacoes: "",
      dataDeComparecimento: "",
      imobiliaria: "",
      contactIdSalesforce: "",
      assuntoParaAgendamento: "",
      gerenteDeVendas: "",
      criadoPor: "",
    },
  ],
  tarefas: [
    {
      opportunityIdSalesforce: "",
      oportunidadeId: "",
      nomeDaConta: "",
      assunto: "",
      dataDeVencimento: "",
      tipoDeRegistro: "",
    },
  ],
};

interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

const Home: React.FC = () => {
  const user: any = localStorage.getItem("@user");
  const navigate = useNavigate();
  const { setPageSidebar, signOut, setAccessChatBot } =
    React.useContext(LoginContext);
  const {
    setHashSalesSidebar,
    setTypeAccount,
    setHashOportunity,
    setHashDetailOportunity,
  } = React.useContext(UsuallyContext);
  const {
    setHomeCreateAccount,
    setAccessCreateOp,
    setIndexTabDetails,
    setIndexTabActivityComponent,
    setRedirectHome,
  } = React.useContext(OportunityContext);

  // Estados
  const [isUnauthorized, setIsUnauthorized] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const [indexCalendarAndTasks, setIndexCalendarAndTasks] = React.useState(0);
  const [showValues, setShowValues] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [dataFixos, setDataFixos] = React.useState<IFixos>(fixos);
  const [dataCalendar, setDataCalendar] =
    React.useState<ICalendario>(calendario);
  const [dataAgendamento, setDataAgendamento] = React.useState<string[]>([""]);
  const [dataTarefas, setDataTarefas] = React.useState<string[]>([""]);
  const [loadingCalendar, setLoadingCalendar] = React.useState(false);
  const [selectedRange, setSelectedRange] = React.useState<DateRange>({
    from: new Date(),
    to: new Date(new Date().setDate(new Date().getDate() + 7)),
  });
  const [isCheckingAccess, setIsCheckingAccess] = React.useState(false);

  // Grid template areas responsivo
  const gridTemplateAreas = useBreakpointValue({
    base: `
      "rating"
      "resumo"
      "oportunidades"
      "agendamentos"
      "datas"
      "comissoes"
    `,
    sm: `
    "rating"
      "resumo"
      "oportunidades"
      "agendamentos"
      "datas"
      "comissoes"
    `,
    md: `
      "resumo resumo"
      "rating oportunidades"
      "agendamentos datas"
      "comissoes comissoes"
    `,
    lg: `
      "rating resumo resumo"
      "agendamentos oportunidades oportunidades"
      "datas comissoes comissoes"
    `,
    xl: `
      "rating resumo resumo"
      "agendamentos oportunidades oportunidades"
      "datas comissoes comissoes"
    `,
  });

  const gridTemplateColumns = useBreakpointValue({
    base: "1fr",
    sm: "1fr",
    md: "1fr 1fr",
    lg: "1.5fr 1.5fr 1.5fr",
    xl: "1.5fr 1.5fr 1.5fr",
    "2xl": "1fr 2fr 2fr",
  });

  // Função memoizada para formatar nome
  const formatNome = React.useCallback((name: string) => {
    if (!name) return "";
    const primeiroNome = name?.split(" ")[0]?.toLowerCase();
    return primeiroNome?.charAt(0)?.toUpperCase() + primeiroNome?.slice(1);
  }, []);

  // Função memoizada para formatar data
  const formatDateToPtBr = React.useCallback((date: Date | undefined) => {
    return date ? format(date, "yyyy-MM-dd", { locale: ptBR }) : "";
  }, []);

  // Array de botões memoizado
  const arrayButtons = React.useMemo(
    () => [
      {
        title: `Criar Conta`,
        icon: <BsPlusLg fontSize={"18px"} />,
        onClick: () => {
          navigate("/oportunidades");
          setPageSidebar("Criar oportunidade");
          localStorage.setItem("@option", "Criar Oportunidade");
          setHashSalesSidebar("Oportunity");
          setTypeAccount("");
          setHomeCreateAccount(true);
        },
      },
      {
        title: `Criar Oportunidade`,
        icon: <BsPlusLg fontSize={"18px"} />,
        onClick: () => {
          navigate("/oportunidades");
          setPageSidebar("Criar oportunidade");
          localStorage.setItem("@option", "Criar Oportunidade");
          setHashSalesSidebar("Oportunity");
        },
      },
    ],
    [
      navigate,
      setHashSalesSidebar,
      setHomeCreateAccount,
      setPageSidebar,
      setTypeAccount,
    ]
  );

  // Tab labels e contents memoizados
  const tabLabels = React.useMemo(
    () => ["Oportunidades por status", "Avaliações de crédito por status"],
    []
  );

  const tabContents = React.useMemo(
    () => ({
      "Oportunidades por status": (
        <CustomOportunityByStatus
          data={
            Array.isArray(dataFixos.oportunidadesPorStatus)
              ? dataFixos.oportunidadesPorStatus
              : []
          }
        />
      ),
      "Avaliações de crédito por status": (
        <CustomCreditRatingByStatus
          data={
            Array.isArray(dataFixos.analiseDeCreditoPorStatus)
              ? dataFixos.analiseDeCreditoPorStatus
              : []
          }
        />
      ),
    }),
    [dataFixos.analiseDeCreditoPorStatus, dataFixos.oportunidadesPorStatus]
  );

  // Função de redirecionamento para agendamento
  const handleRedirectSchedulle = React.useCallback(
    (oportunidadeId: string, opportunityIdSalesforce: string) => {
      setPageSidebar("Minhas oportunidades");
      localStorage.setItem("@option", "Minhas oportunidades");

      setHashOportunity("DetailOportunityComponent");
      setHashDetailOportunity("DetailOportunity");
      setRedirectHome("schedule");
      setIndexTabDetails(0);

      setAccessCreateOp((prevState: any) => ({
        ...prevState,
        oportunidadeId: oportunidadeId,
        oportunityIdSalesforce: opportunityIdSalesforce,
      }));

      navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
        state: {
          tabDetails: 0,
          tabActivity: 1,
          source: "schedule",
          oportunidadeId,
        },
      });
    },
    [
      navigate,
      setAccessCreateOp,
      setHashDetailOportunity,
      setHashOportunity,
      setIndexTabDetails,
      setPageSidebar,
      setRedirectHome,
    ]
  );

  // Função de redirecionamento para tarefas
  const handleRedirectTasks = React.useCallback(
    (oportunidadeId: string, opportunityIdSalesforce: string) => {
      setPageSidebar("Minhas oportunidades");
      localStorage.setItem("@option", "Minhas oportunidades");

      setHashOportunity("DetailOportunityComponent");
      setHashDetailOportunity("DetailOportunity");
      setRedirectHome("task");
      setIndexTabDetails(0);

      setAccessCreateOp((prevState: any) => ({
        ...prevState,
        oportunidadeId: oportunidadeId,
        oportunityIdSalesforce: opportunityIdSalesforce,
      }));

      navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
        state: {
          tabDetails: 0,
          tabActivity: 0,
          source: "task",
          oportunidadeId,
        },
      });
    },
    [
      navigate,
      setAccessCreateOp,
      setHashDetailOportunity,
      setHashOportunity,
      setIndexTabDetails,
      setPageSidebar,
      setRedirectHome,
    ]
  );

  const tabLabelsSchedulleAndTasks = React.useMemo(
    () => ["Agendamentos", "Tarefas"],
    []
  );

  const tabContentsSchedulleAndTasks = React.useMemo(
    () => ({
      Agendamentos: (
        <CustomSchedulle
          dataCalendar={dataCalendar}
          loadingCalendar={loadingCalendar}
          onClick={handleRedirectSchedulle}
        />
      ),
      Tarefas: (
        <CustomTasks
          dataCalendar={dataCalendar}
          loadingCalendar={loadingCalendar}
          onClick={handleRedirectTasks}
        />
      ),
    }),
    [
      dataCalendar,
      handleRedirectSchedulle,
      handleRedirectTasks,
      loadingCalendar,
    ]
  );

  // Total de comissão memoizado
  const totalComission = React.useMemo(
    () =>
      dataFixos?.comissoes?.reduce(
        (sum, item) => sum + (item?.valorHonorarios || 0),
        0
      ) || 0,
    [dataFixos?.comissoes]
  );

  // Função para buscar dados fixos
  const handleGetFixos = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await homeServiceInstance.getFixos();

      if (response.status >= 400 && response?.status !== 401) {
        setLoading(false);
        setDataFixos(fixos);
        console.log("status 400", response);
        return;
      }

      if (response.status === 401) {
        setLoading(false);
        setIsUnauthorized(true);
        return;
      }

      setLoading(false);
      setDataFixos(response);
    } catch (error) {
      setLoading(false);
      console.log(error);
      setDataFixos(fixos);
    }
  }, []);

  // Função para buscar dados do calendário com debounce
  const handleGetCalendar = React.useCallback(async () => {
    if (!selectedRange.from || !selectedRange.to) return;

    setLoadingCalendar(true);
    try {
      const response = await homeServiceInstance.getScheduledByDate({
        dataInicio: formatDateToPtBr(selectedRange.from),
        dataFim: formatDateToPtBr(selectedRange.to),
      });

      if (response.status >= 400 && response?.status !== 401) {
        setLoadingCalendar(false);
        setDataCalendar(calendario);
        setDataAgendamento([""]);
        setDataTarefas([""]);
        console.log("status 400", response);
        return;
      }

      if (response.status === 401) {
        setLoadingCalendar(false);
        return;
      }

      setLoadingCalendar(false);
      setDataCalendar(response);

      setDataAgendamento(
        response?.agendamentos?.map((data: any) => data.dataAgendamento)
      );
      setDataTarefas(
        response?.tarefas?.map((data: any) => data.dataDeVencimento)
      );
    } catch (error) {
      setLoadingCalendar(false);
      console.log(error);
      setDataCalendar(calendario);
      setDataAgendamento([""]);
      setDataTarefas([""]);
    }
  }, [formatDateToPtBr, selectedRange.from, selectedRange.to]);

  // useEffect com debounce para calendário
  React.useEffect(() => {
    const timer = setTimeout(() => {
      handleGetCalendar();
    }, 300);

    return () => clearTimeout(timer);
  }, [handleGetCalendar]);

  // useEffect para dados fixos
  React.useEffect(() => {
    handleGetFixos();
  }, [handleGetFixos]);

  // useEffect para verificar acesso ao chatbot
  React.useEffect(() => {
    const checkAccessChatBot = async () => {
      const regional = localStorage.getItem("@regional") || "";
      setIsCheckingAccess(true);
      setAccessChatBot(false);
      try {
        const response = await chatServiceInstance.getAccessChatBot(regional);
        setAccessChatBot(response);
      } catch (error) {
        console.error("Erro ao verificar acesso ao chatbot:", error);
        setAccessChatBot(false);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    checkAccessChatBot();
  }, [setAccessChatBot]);

  // Componente de loading
  const LoadingComponent = React.useMemo(
    () => (
      <Flex h={"71vh"} align={"center"} justify={"center"}>
        <Spinner />
      </Flex>
    ),
    []
  );

  // Componente de header responsivo
  const HeaderComponent = React.useMemo(
    () => (
      <Flex
        flexDir={{ base: "column", md: "row" }}
        w={"100%"}
        h={{ base: "auto", md: "80px" }}
        alignItems={"center"}
        mb={{ base: 6, md: 8 }}
        gap={{ base: 4, md: 0 }}
        px={{ base: 0, md: 0 }}
      >
        <Box
          display={"flex"}
          flexDir={"column"}
          alignItems={{ base: "center", md: "flex-start" }}
          w={{ base: "100%", md: "85%" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Text
            fontSize={{ base: 22, sm: 24, md: 28 }}
            fontWeight={"semibold"}
            color={theme.text}
          >
            Olá, {formatNome(user)}
          </Text>
          <Text
            fontSize={{ base: 12, sm: 13, md: 14 }}
            color={theme.text50}
            maxW={{ base: "100%", md: "100%" }}
            px={{ base: 2, md: 0 }}
          >
            Confira um resumo das suas oportunidades, agendamentos e comissões
          </Text>
        </Box>

        <Flex
          w={{ base: "100%", sm: "100%", md: "360px" }}
          mr={{ base: 0, md: 15 }}
          gap={"8px"}
          justify={{ base: "center", md: "flex-end" }}
          flexDir={{ base: "column", sm: "row" }}
          px={{ base: 0, sm: 0 }}
        >
          <Button
            title={
              <Flex
                align={"center"}
                justify={"center"}
                fontSize={{ base: "sm", md: "md" }}
              >
                Diri Academy
                <Box fontSize={{ base: "16px", md: "18px" }} w={"fit-content"}>
                  <MdOutlineArrowOutward />
                </Box>
              </Flex>
            }
            colorInverter
            onClick={() =>
              window.open("https://diriacademy.skore.io/login", "_blank")
            }
          />

          <ButtonPopover
            title={"Criar"}
            popoverContent={arrayButtons.map((button) => (
              <Box w="100%" key={button.title}>
                <Flex flexDir={"column"} w="100%" justifyContent={"center"}>
                  <Box
                    display={"flex"}
                    p={"10px"}
                    gap={"15px"}
                    alignItems={"center"}
                    opacity={"0.8"}
                    _hover={{ opacity: "1" }}
                    cursor="pointer"
                    onClick={button.onClick}
                  >
                    <Box w={"fit-content"} color={theme.text}>
                      {button.icon}
                    </Box>
                    <Text
                      w={"fit-content"}
                      fontSize={"13px"}
                      fontWeight={"medium"}
                      color={theme.text}
                      _hover={{ color: theme.primary }}
                    >
                      {button.title}
                    </Text>
                  </Box>

                  <Divider
                    orientation="horizontal"
                    borderColor={"#E5E9F0"}
                    h={"2px"}
                  />
                </Flex>
              </Box>
            ))}
          />
        </Flex>
      </Flex>
    ),
    [user, formatNome, arrayButtons]
  );

  return (
    <Box
      display={"flex"}
      pos={"relative"}
      flexDir={"column"}
      p={{ base: 3, sm: 4, md: 6, lg: 8 }}
      left={{ base: 0, lg: "230px" }}
      mt={{ base: "70px", sm: "50px", md: "20px", lg: "0px" }}
      w={{ base: "100%", lg: "calc(100% - 230px)" }}
      overflowX={"hidden"}
      bg={theme.disable100}
      minH={"100vh"}
    >
      <Alert
        onOpen={isUnauthorized}
        title="Sessão expirada"
        titleButton={"Fazer login novamente"}
        description={
          "Sua sessão expirou, faça login novamante para ter acesso."
        }
        onClick={() => {
          signOut();
        }}
      />

      {HeaderComponent}

      <Container maxW={{ base: "100%", md: "100%", "2xl": "80rem" }} p={0}>
        {loading ? (
          LoadingComponent
        ) : (
          <Grid
            templateAreas={gridTemplateAreas}
            gridTemplateColumns={gridTemplateColumns}
            gap={{ base: 4, sm: 4, md: 6 }}
            w="100%"
            px={{ base: 0, sm: 0 }}
          >
            {/* Oportunidades pendentes */}
            <GridItem
              area="resumo"
              bg="white"
              p={{ base: 4, sm: 5, md: 6 }}
              borderRadius="lg"
              shadow="md"
              w={"100%"}
              h={{ base: "160px", sm: "160px", md: "180px" }}
            >
              <Text
                fontWeight={"semibold"}
                color={theme.text}
                mb={3}
                fontSize={{ base: "md", md: "lg" }}
              >
                Oportunidades pendentes
              </Text>
              <CustomResume
                data={
                  dataFixos.oportunidadesPendentes
                    ? dataFixos.oportunidadesPendentes
                    : []
                }
              />
            </GridItem>

            {/* Rating */}
            <GridItem
              area="rating"
              bg="white"
              p={{ base: 4, sm: 5, md: 6 }}
              borderRadius="lg"
              shadow="md"
              w={"100%"}
              h={{ base: "200px", sm: "200px", md: "310px" }}
            >
              <CustomRating
                dataRating={
                  Array.isArray(dataFixos.oportunidadesPorRanking)
                    ? dataFixos.oportunidadesPorRanking
                    : []
                }
              />
            </GridItem>

            {/* Oportunidades por status */}
            <GridItem
              area="oportunidades"
              bg="white"
              p={{ base: 2, sm: 2, md: 2 }}
              borderRadius="lg"
              shadow="md"
              mt={{ base: "0", md: "-130px" }}
              h={{ base: "380px", sm: "400px", md: "370px" }}
            >
              <Box w="100%">
                <Tab
                  widthXl="100%"
                  width="100%"
                  widthMd="100%"
                  widthSm="100%"
                  widthBase="100%"
                  index={index}
                  setIndex={setIndex}
                  tabLabels={tabLabels}
                  tabContents={tabContents}
                />
              </Box>
            </GridItem>

            {/* Calendario */}
            <GridItem
              display={"flex"}
              flexDir={"column"}
              area="agendamentos"
              bg="white"
              p={{ base: 2, sm: 2, md: 2 }}
              borderRadius="lg"
              shadow="md"
              w={{ base: "100%", xl: "auto", "2xl": "350px" }}
              h={{ base: "350px", sm: "360px", md: "330px" }}
              overflowX={"auto"}
              alignItems={"center"}
              justifyContent={{
                base: "flex-start",
                sm: "flex-start",
                md: "initial",
              }}
              css={{
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#D3D3D3",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: "#A0AEC0",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#F7FAFC",
                },
              }}
            >
              <Flex
                w={{ base: "100%", xl: "100%", "2xl": "100%" }}
                justify="center"
              >
                <CustomCalendar
                  selectedRange={selectedRange}
                  setSelectedRange={setSelectedRange}
                  dataAgendamento={dataAgendamento}
                  dataTarefas={dataTarefas}
                />
              </Flex>
            </GridItem>

            {/* Agendamentos e Tarefas */}
            <GridItem
              area="datas"
              bg="white"
              p={2}
              borderRadius="lg"
              shadow="md"
              h={{ base: "220px", sm: "240px", md: "auto" }}
            >
              <Box w="100%">
                <Tab
                  widthXl="100%"
                  width="100%"
                  widthMd="100%"
                  widthSm="100%"
                  widthBase="100%"
                  index={indexCalendarAndTasks}
                  setIndex={setIndexCalendarAndTasks}
                  tabLabels={tabLabelsSchedulleAndTasks}
                  tabContents={tabContentsSchedulleAndTasks}
                />
              </Box>
            </GridItem>

            {/* Próximas Comissões */}
            <GridItem
              area="comissoes"
              display={"flex"}
              flexDir={"column"}
              bg="white"
              p={{ base: 4, sm: 5, md: 6 }}
              borderRadius="lg"
              shadow="md"
              mt={{ base: "0px", md: "0px", lg: "-90px" }}
              h={{ base: "320px", sm: "340px", md: "310px" }}
            >
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontWeight={"semibold"}
                color={theme.text}
              >
                Próximas comissões
              </Text>

              <Text
                mb={3}
                fontSize={{ base: "12px", sm: "13px", md: "14px" }}
                color={theme.text}
              >
                Você tem um total de{" "}
                <Text as="span" fontWeight="bold">
                  {showValues ? `${formatMoney(totalComission)}` : "R$ •••••"}
                </Text>{" "}
                a receber
              </Text>

              <Box
                overflowY={"auto"}
                h={{ base: "220px", sm: "240px", md: "200px" }}
                css={{
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#D3D3D3",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#A0AEC0",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#F7FAFC",
                  },
                }}
              >
                <CustomComitions
                  data={
                    Array.isArray(dataFixos.comissoes)
                      ? dataFixos.comissoes
                      : []
                  }
                  showValues={showValues}
                  setShowValues={setShowValues}
                />
              </Box>
            </GridItem>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default React.memo(Home);
